/**
 * The desk's one Download menu: how it sounds, to send or to keep, and the notes, to edit.
 *
 * One menu named for what it does rather than a row of "Save …" buttons, because the buttons
 * were looked straight past: nothing on them said download, and nothing said which file was
 * the one to send someone. Each item says what it is for.
 *
 * The audio is rendered when it is asked for, on the same instruments the desk plays on and
 * at the speed the score is marked (`lib/render-audio.ts`), which takes a few seconds for a
 * long piece; the button says so while it does, and says why when it could not.
 */

import { useCallback, useRef, useState } from "react";
import type { PlayerOptions, Score, ScheduleOptions } from "@app/score-editor";
import { ACTION } from "./Chrome";
import { renderAudio } from "../lib/render-audio";
import type { AudioFormat } from "../lib/render-audio";
import { download } from "../lib/score-files";
import { useDismiss } from "../lib/use-dismiss";
import { useSampledEngine } from "../lib/use-sampled-engine";

export interface ScoreDownloadsProps {
  /** The score the audio is played from: every bar, where the desk draws fewer. */
  readonly sound: Score;
  readonly options: ScheduleOptions & PlayerOptions;
  readonly musicxml: string;
  /** The MusicXML file's name; the audio is named after it. */
  readonly fileName: string;
}

/** What each audio format is saved as. */
const AUDIO: Readonly<Record<AudioFormat, { type: string; label: string }>> = {
  mp3: { type: "audio/mpeg", label: "MP3" },
  wav: { type: "audio/wav", label: "WAV" },
};

const ITEM =
  "flex w-full flex-col items-start rounded px-2.5 py-1.5 text-left hover:bg-shell-hot focus-visible:bg-shell-hot focus-visible:outline-none";

function Item({
  title,
  detail,
  onPick,
}: {
  title: string;
  detail: string;
  onPick(): void;
}): JSX.Element {
  return (
    <button type="button" role="menuitem" onClick={onPick} className={ITEM}>
      <span className="text-[12px] text-shell-lit">{title}</span>
      <span className="text-[11px] text-shell-dim">{detail}</span>
    </button>
  );
}

export default function ScoreDownloads({
  sound,
  options,
  musicxml,
  fileName,
}: ScoreDownloadsProps): JSX.Element {
  const { wake } = useSampledEngine();
  const [open, setOpen] = useState(false);
  const [writing, setWriting] = useState<AudioFormat | null>(null);
  const [error, setError] = useState<string | null>(null);
  const menu = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(menu, open, close);

  const saveNotes = (): void => {
    close();
    download(fileName, "application/vnd.recordare.musicxml+xml", musicxml);
  };

  const saveAudio = (format: AudioFormat): void => {
    close();
    setWriting(format);
    setError(null);
    renderAudio(wake(), sound, options, format)
      .then((bytes) =>
        download(`${fileName.replace(/\.[^.]*$/, "")}.${format}`, AUDIO[format].type, bytes),
      )
      .catch((reason: unknown) =>
        setError(reason instanceof Error ? reason.message : String(reason)),
      )
      .finally(() => setWriting(null));
  };

  return (
    <div className="ml-auto flex min-w-0 items-center gap-2">
      {error === null ? null : (
        <p className="truncate font-mono text-[11px] text-fault" title={error}>
          The audio was not written: {error}
        </p>
      )}
      <div ref={menu} className="relative shrink-0">
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          disabled={writing !== null}
          onClick={() => setOpen(!open)}
          className={`${ACTION} gap-1.5 border border-shell-seam aria-expanded:bg-shell-hot aria-expanded:text-shell-lit disabled:cursor-wait disabled:text-shell-dim`}
        >
          <span aria-hidden="true">↓</span>
          {writing === null ? "Download" : `Writing ${AUDIO[writing].label}…`}
          <span aria-hidden="true" className="text-shell-dim">
            ▾
          </span>
        </button>
        {!open ? null : (
          <div
            role="menu"
            aria-label="Download"
            className="absolute right-0 top-full z-30 mt-1 w-60 rounded-md border border-shell-edge bg-shell-bar p-1 shadow-chrome"
          >
            <Item
              title="Audio · MP3"
              detail="Small, to send someone"
              onPick={() => saveAudio("mp3")}
            />
            <Item
              title="Audio · WAV"
              detail="Full quality, several times larger"
              onPick={() => saveAudio("wav")}
            />
            <div className="my-1 border-t border-shell-edge" />
            <Item
              title="Score · MusicXML"
              detail="To open in a notation program"
              onPick={saveNotes}
            />
          </div>
        )}
      </div>
    </div>
  );
}
